import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Cabecera } from '#components/mobile/ui/Cabecera';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { Pildora } from '#components/mobile/ui/Pildora';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useEnableBankingStatus } from '#hooks/useEnableBankingStatus';
import { useNavigate } from '#hooks/useNavigate';

type Entrada = {
  nombre: string;
  ruta: string;
  icono: NombreIcono;
  detalle?: string;
  /** En gris, sin tocar, con «Próximamente» (lo que aún no tienen todos). */
  proximamente?: boolean;
};

/**
 * Lista de accesos de «Más». B8 · H58: cada destino con su icono, sin
 * repetir los de la brújula (`cal` es «Cambiar de mes», `sliders` es
 * «Filtros», `refresh` es «Reabrir cuenta»). Ajustes ya no la repite (H86).
 */
export const ICONOS_MAS = {
  '/anual': 'calclock',
  '/bitacora': 'note',
  '/payees': 'store',
  '/bank-sync': 'bank',
  '/settings': 'settings',
} as const satisfies Record<string, NombreIcono>;

/**
 * Entradas de «Más». El banco solo funciona para el admin: el resto ve
 * «Conectar con el banco» en gris con «Próximamente» (Vadik, 1-nov-2026).
 */
export function entradasMas({
  t,
  conBanca,
  sinAjustes,
}: {
  t: (texto: string) => string;
  /** `null` mientras no se sabe: sin fila del banco, ni gris ni normal. */
  conBanca: boolean | null;
  sinAjustes: boolean;
}): Entrada[] {
  return [
    {
      nombre: t('Fixed payments'),
      ruta: '/anual',
      icono: ICONOS_MAS['/anual'],
    },
    {
      nombre: t('Your logbook'),
      ruta: '/bitacora',
      icono: ICONOS_MAS['/bitacora'],
    },
    { nombre: t('Payees'), ruta: '/payees', icono: ICONOS_MAS['/payees'] },
    // Sin «Reglas»: trabajan solas y no se enseñan (Vadik, 19-oct-2026).
    ...(conBanca === null ? [] : [bancoEnMas(t, conBanca)]),
    ...(sinAjustes
      ? []
      : [
          {
            nombre: t('Settings'),
            ruta: '/settings',
            icono: ICONOS_MAS['/settings'],
          },
        ]),
  ];
}

function bancoEnMas(t: (texto: string) => string, conBanca: boolean): Entrada {
  return conBanca
    ? {
        nombre: t('Bank Sync'),
        ruta: '/bank-sync',
        icono: ICONOS_MAS['/bank-sync'],
      }
    : {
        nombre: t('Connect to the bank'),
        ruta: 'banco-proximamente',
        icono: ICONOS_MAS['/bank-sync'],
        detalle: t('Your transactions will come in by themselves'),
        proximamente: true,
      };
}

export function ListaMas({ sinAjustes = false }: { sinAjustes?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bancaPermitida, isLoading } = useEnableBankingStatus();
  const entradas = entradasMas({
    t,
    conBanca: isLoading ? null : bancaPermitida,
    sinAjustes,
  });

  return (
    <Tarjeta relleno={0} data-testid="lista-mas">
      {entradas.map((e, i) => (
        <View
          key={e.ruta}
          style={{ borderTop: i === 0 ? 0 : `1px solid ${color.line}` }}
        >
          <Tarjeta
            relleno={0}
            onPress={e.proximamente ? undefined : () => void navigate(e.ruta)}
            style={{
              borderRadius: 0,
              boxShadow: 'none',
              flexDirection: 'row',
              alignItems: 'center',
              gap: espacio.icono,
              minHeight: 56,
              padding: '0 14px',
            }}
          >
            <IconoCaja
              icono={e.icono}
              size={36}
              style={e.proximamente ? { opacity: 0.55 } : undefined}
            />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: e.proximamente ? color.fg3 : undefined,
                }}
              >
                {e.nombre}
              </Text>
              {e.detalle && (
                <Text style={{ fontSize: 12.5, color: color.fg3 }}>
                  {e.detalle}
                </Text>
              )}
            </View>
            {e.proximamente ? (
              <Pildora estado="aviso">
                <Trans>Coming soon</Trans>
              </Pildora>
            ) : (
              <Icono nombre="cr" size={18} style={{ color: color.fg3 }} />
            )}
          </Tarjeta>
        </View>
      ))}
    </Tarjeta>
  );
}

/**
 * «Más»: lo que no cabe en la barra de pestañas (Pagos fijos,
 * Beneficiarios, Reglas, Sincronización bancaria y Ajustes). Se llega desde
 * «Más», la última opción de la brújula en todas las pantallas (aquí no sale).
 * Cabecera con identidad, como las pestañas: subtítulo que dice qué hay.
 */
export function MasPage() {
  const { t } = useTranslation();
  return (
    <Page
      header={
        <Cabecera
          subtitulo={t('Payments, payees and settings')}
          titulo={t('More')}
          style={{ paddingBottom: 10 }}
        />
      }
      padding={0}
    >
      <View
        data-testid="mas"
        style={{
          padding: espacio.margen,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
        }}
      >
        <ListaMas />
      </View>
    </Page>
  );
}
